<template>
  <div class="dashboard-infos__offers infos-offers">
    <div class="infos-offers__header offers-header">
      <h2 class="offers-header__title">
        {{ trans('addresses.details.dashboard.offers.title') }}
      </h2>
      <a class="offers-header__link" :href="offersLink">{{ trans('addresses.details.dashboard.offers.link') }}</a>
    </div>
    <div class="infos-offers__content offers-content">
      <div
        class="offer-content__chart content-chart"
        @mousemove="updateTooltipsPosition"
      >
        <div class="content-chart__total">
          <span>{{ total }}</span>
        </div>
        <canvas ref="chart" class="content-chart__canvas" />
        <div
          v-for="(statuses, groupStatus) in hoverChartData"
          :key="groupStatus"
          ref="tooltips"
          class="content-chart__legend chart-legend"
          @mouseenter="disableTooltipHandler"
          @mouseleave="enableTooltipHandler"
        >
          <div
            v-for="(count, status) in statuses"
            :key="status"
            class="chart-legend__row legend-row"
          >
            <div class="legend-row__value" :class="`row-value--${groupStatus}`">
              <span>{{ count }}</span>
            </div>
            <p class="legend-row__text">
              {{ getStatusText(status, count) }}
            </p>
          </div>
        </div>
      </div>
      <div class="offer-content__legend content-legend">
        <div
          v-for="(count, status) in mainChartData"
          :key="status"
          class="content-legend__row legend-row"
        >
          <div class="legend-row__value" :class="`row-value--${status}`">
            <span>{{ count }}</span>
          </div>
          <p class="legend-row__text">
            {{ getGroupStatusText(status) }}
          </p>
        </div>
      </div>
    </div>
    <div v-if="displayUpdateAlert" class="infos-offers__alert offers-alert panel panel--warning">
      <div class="offers-alert__icon panel__icon panel__icon--ph_warning-fill-small_yellow" />
      <div class="panel__group">
        <p class="offers-alert__title">
          {{ trans('addresses.details.dashboard.offers.alert.title') }}
        </p>
        <a :href="offersLink" class="offers-alert__link">
          {{ trans('addresses.details.dashboard.offers.alert.link') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { findIndex, keys, pick, reverse, sum, values } from 'lodash-es';
import events from '@elements/events';
import { URL_USER_OFFERS } from '@tools/routes/routes';
import translation from '@elements/translation';

Chart.register(...registerables);
export default {
  mixins: [ events, translation ],
  props: {
    address: { type: Object, required: true },
  },
  data() {
    const chartData = window.CHART_DATA;
    const mainChartData = pick(chartData, [ 'online', 'pending', 'offline' ]);
    const hoverChartData = {
      online: pick(chartData, [ 'published', 'offmarket' ]),
      pending: pick(chartData, [ 'moderation' ]),
      offline: pick(chartData, [ 'draft', 'expired', 'blocked', 'unpublished' ]),
    };
    const style = getComputedStyle(document.body);
    const total = sum(values(mainChartData));
    const emptyChart = total === 0;
    return {
      mainChartData,
      hoverChartData,
      total,
      displayUpdateAlert: window.DISPLAY_UPDATE_ALERT,
      offersLink: `${URL_USER_OFFERS}?addresses=${this.address.id}`,
      mobileLimit: 7,
      desktopLimit: 30,
      chartInstance: null,
      tooltipHandlerDisabled: false,
      chartConfig: {
        type: 'doughnut',
        options: {
          borderWidth: 0,
          cutout: '60%',
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: this.tooltipHandler,
              intersect: 'interaction.intersect',
            },
          },
          hover: { mode: null },
        },
        data: {
          datasets: [ {
            data: emptyChart ? [ 1, 0, 0 ] : reverse(values(mainChartData)),
            backgroundColor: [
              style.getPropertyValue('--color-gray'),
              style.getPropertyValue('--color-yellow'),
              style.getPropertyValue('--color-green'),
            ],
            hoverOffset: 4,
          } ],
          labels: emptyChart ? [ '', '', '' ] : reverse(keys(mainChartData)),
        },
      },
    };
  },
  mounted() {
    this.createChart();
  },
  methods: {
    enableTooltipHandler() {
      this.tooltipHandlerDisabled = false;
      for (let i = 0; i < this.$refs.tooltips.length; i++) {
        this.$refs.tooltips[i].style.opacity = '0';
      }
    },
    disableTooltipHandler() {
      this.tooltipHandlerDisabled = true;
    },
    createChart() {
      this.chartInstance = new Chart(this.$refs.chart, this.chartConfig);
    },
    updateTooltipsPosition(event) {
      for (let i = 0; i < this.$refs.tooltips.length; i++) {
        this.$refs.tooltips[i].style.left = `${event.clientX + 16}px`;
        this.$refs.tooltips[i].style.top = `${event.clientY + 16}px`;
      }
    },
    getGroupStatusText(status) {
      return this.trans(`addresses.details.dashboard.offers.${status}`);
    },
    getStatusText(status, count) {
      return this.transchoice(`offer.state.${status}`, { count });
    },
    tooltipHandler(context) {
      if (this.tooltipHandlerDisabled) {
        return;
      }

      const { tooltip } = context;
      const status = tooltip.dataPoints[0]?.label;
      const index = findIndex(keys(this.mainChartData), (val) => val === status);

      for (let i = 0; i < this.$refs.tooltips.length; i++) {
        this.$refs.tooltips[i].style.opacity = (status && i === index && tooltip.opacity === 1) ? '1' : '0';
      }
    },

  },
};
</script>
